import { noplProductFilter } from "./noplProductFilter"
import { isProductSalesRestrictedForCustomer } from "./productMasterSalesRestriction"

export {
  computeAlgoliaCustomerIds,
  getSalesRestrictionBannerInfo,
  isProductMasterVisibleToCustomer,
  isProductSalesRestrictedForCustomer,
  productMasterSalesRestrictionFetchAttributes,
} from "./productMasterSalesRestriction"
export type { ISalesRestrictionBannerInfo } from "./productMasterSalesRestriction"

type IProductGroupMasterRestriction = {
  sales_restricted?: boolean
  allowed_sales_customers?: { id: string }[]
}

// --- Skeem filters (product / product_group / product_group_master) ---

/** Include products not in a restricted PGM, or in one where the customer is on the bypass list. */
export const salesRestrictedProductFilter = (customerId?: string) => ({
  product: {
    or: [
      {
        not: {
          exists: {
            path: [
              "product_group",
              { name: "product_group_master", filter: { matches: { sales_restricted: true } } },
            ],
          },
        },
      },
      customerId
        ? {
            exists: {
              path: [
                "product_group",
                {
                  name: "product_group_master",
                  filter: {
                    and: [
                      { matches: { sales_restricted: true } },
                      {
                        exists: {
                          path: [
                            {
                              name: "allowed_sales_customers",
                              filter: { matches: { id: customerId } },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          }
        : { value: false },
    ],
  },
})

function salesRestrictionBypassExistsFilter(customerIds: string[]) {
  return {
    exists: {
      path: [
        {
          name: "allowed_sales_customers",
          filter: { matches: { id: { or: customerIds } } },
        },
      ],
    },
  }
}

/** Shared OR clause for product_group_masters: unrestricted PGM or customer on bypass list. */
function salesRestrictionOrClause(customerIds: string[]) {
  return {
    or: [
      { not: { matches: { sales_restricted: true } } },
      salesRestrictionBypassExistsFilter(customerIds),
    ],
  }
}

export const salesRestrictedProductGroupFilter = (customerIds: string[]) => {
  if (customerIds.length === 0) {
    return undefined
  }
  return {
    or: [
      {
        not: {
          exists: {
            path: [
              { name: "product_group_master", filter: { matches: { sales_restricted: true } } },
            ],
          },
        },
      },
      {
        exists: {
          path: [
            {
              name: "product_group_master",
              filter: {
                and: [
                  { matches: { sales_restricted: true } },
                  salesRestrictionBypassExistsFilter(customerIds),
                ],
              },
            },
          ],
        },
      },
    ],
  }
}

export const salesRestrictedProductGroupMasterFilter = (customerIds: string[]) => {
  if (customerIds.length === 0) {
    return undefined
  }
  return salesRestrictionOrClause(customerIds)
}

/** Skeem filter: master has at least one product not locked to bypass-only sales. */
export const salesRestrictedMasterIndexFilter = {
  exists: {
    path: [
      {
        name: "products",
        filter: {
          or: [
            {
              not: {
                exists: {
                  path: [
                    "product_group",
                    {
                      name: "product_group_master",
                      filter: { matches: { sales_restricted: true } },
                    },
                  ],
                },
              },
            },
            {
              exists: {
                path: [
                  "product_group",
                  {
                    name: "product_group_master",
                    filter: {
                      exists: { path: [{ name: "allowed_sales_customers" }] },
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  },
}

// --- Composites ---

export const customerProductFilter = (customerId?: string) => ({
  product: {
    and: [noplProductFilter(customerId).product, salesRestrictedProductFilter(customerId).product],
  },
})

const excludeProductMasterSearchBranches = ["test", "demo", "training", "hub", "ssp"]

const excludeProductMasterSearchBranchesProductFilter = {
  not: {
    exists: {
      path: [
        {
          name: "branch",
          filter: { matches: { name: { or: excludeProductMasterSearchBranches } } },
        },
      ],
    },
  },
}

/** Product is sales-restricted for this customer (negative form of salesRestrictedProductFilter). */
const productSalesRestrictedForCustomerFilter = (customerId?: string) => ({
  exists: {
    path: [
      "product_group",
      {
        name: "product_group_master",
        filter: {
          and: [
            { matches: { sales_restricted: true } },
            customerId
              ? {
                  not: {
                    exists: {
                      path: [
                        {
                          name: "allowed_sales_customers",
                          filter: { matches: { id: customerId } },
                        },
                      ],
                    },
                  },
                }
              : { value: true },
          ],
        },
      },
    ],
  },
})

export const customerVisibleProductMasterFilter = (
  customerId?: string,
  branchId?: string | null,
) => ({
  and: [
    noplProductFilter(customerId).productMasterNegative,
    {
      not: {
        exists: {
          path: [
            {
              name: "products",
              filter: {
                and: [
                  productSalesRestrictedForCustomerFilter(customerId),
                  excludeProductMasterSearchBranchesProductFilter,
                  branchId && { matches: { branch: branchId } },
                ].filter(Boolean),
              },
            },
          ],
        },
      },
    },
  ],
})

// --- Runtime ---

export async function fetchSalesRestrictedProductIds(
  skeem: typeof window.skeem,
  productIds: string[],
  customerId: string,
): Promise<Set<string>> {
  if (productIds.length === 0) {
    return new Set()
  }
  const products = await skeem.fetch2<
    {
      id: string
      product_group?: {
        product_group_master?: IProductGroupMasterRestriction
      }
    }[]
  >({
    model: {
      name: "products",
      attributes: [
        {
          name: "product_group",
          attributes: [
            {
              name: "product_group_master",
              attributes: ["sales_restricted", "allowed_sales_customers"],
            },
          ],
        },
      ],
      filter: { matches: { id: { or: productIds } } },
    },
  })
  return new Set(
    products
      .filter(product => isProductSalesRestrictedForCustomer(product, customerId))
      .map(product => product.id),
  )
}
